import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { assertSupabaseConfigured, supabase } from "../lib/supabase";
import type { AuthCredentials } from "../types/auth";

function callbackUrl(path: string) {
  const configuredBaseUrl = import.meta.env.VITE_APP_BASE_URL?.trim();
  const rawBaseUrl = configuredBaseUrl || window.location.origin;
  const baseUrlWithProtocol = /^https?:\/\//i.test(rawBaseUrl)
    ? rawBaseUrl
    : `https://${rawBaseUrl}`;
  const baseUrl = new URL(baseUrlWithProtocol).origin;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${baseUrl}${normalizedPath}`;
}

export const authService = {
  async getCurrentSession(): Promise<Session | null> {
    assertSupabaseConfigured();

    const { data, error } = await supabase.auth.getSession();

    if (error) {
      throw error;
    }

    return data.session;
  },

  onAuthStateChange(
    callback: (event: AuthChangeEvent, session: Session | null) => void,
  ) {
    assertSupabaseConfigured();
    return supabase.auth.onAuthStateChange(callback);
  },

  async signIn({ email, password }: AuthCredentials) {
    assertSupabaseConfigured();

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error) {
      throw error;
    }

    return data;
  },

  async sendPasswordReset(email: string) {
    assertSupabaseConfigured();

    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: callbackUrl("/reset-password"),
    });

    if (error) {
      throw error;
    }
  },

  async updatePassword(password: string) {
    assertSupabaseConfigured();

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      throw error;
    }
  },

  async signOut() {
    assertSupabaseConfigured();

    const { error } = await supabase.auth.signOut();

    if (error) {
      throw error;
    }
  },
};
